// client/src/components/AnnouncementsHome.js
//
// App-level home. Renders the active org's announcements across every scope
// (org / age_group / team). Sorted server-side; we just render.
//
// Design decisions worth remembering:
//   - Byline uses inline scope context: "Coach · Puffins · 2h ago"
//   - Pinned cards get a yellow Highlight on part of the title + a purple
//     "NEED TO KNOW!" Sticker top-right. Position-at-top is server-sorted,
//     so we don't re-sort in JS; we just style whatever comes first.
//   - Expired announcements stay in the feed but render muted (opacity-60,
//     no sticker even if pinned). Decided this with Jess: coaches want
//     scrollback access, hiding is too aggressive.
//   - Author name is deferred — we show "Coach" (generic role placeholder)
//     until proper role-aware profile hydration lands. Only Coaches/Admins
//     can post, so "Coach" is honest enough for now.
//


import { useFetch } from '../hooks/useFetch';
import { useOrg } from '../lib/OrgProvider';
import { PfShell } from './shell/PfShell';
import { PageTitle } from './shell/PageTitle';
import { Scrawl } from './mischief/Scrawl';
import { Highlight } from './mischief/Highlight';
import { Sticker } from './mischief/Sticker';
import { MarkerCircle } from './mischief/MarkerCircle';
import { Link } from 'react-router-dom';
import { Megaphone } from './icons/Megaphone';

// ── timeAgo ─────────────────────────────────────────────────────────────
// Relative for < 7 days, absolute month/day after. Kept local to this
// screen — promote to src/lib/ if a second screen needs it.
//
// Catalyst returns datetimes as "YYYY-MM-DD HH:MM:SS" (no TZ). We treat
// them as UTC — which matches what Catalyst stores. If a future timezone
// bug shows up ("posted 2 hours in the future"), that's the seam.
function timeAgo(iso) {
  if (!iso) return '';
  // Catalyst returns "YYYY-MM-DD HH:MM:SS" without a timezone. Parse
  // as LOCAL time — schedules and publish times are stored as local
  // wall-clock, not UTC. See ScheduleScreen's toDate() for context.
  const normalized = iso.includes('T') ? iso : iso.replace(' ', 'T');
  const then = new Date(normalized).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));

  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  // Older than a week — show absolute short date.
  const d = new Date(normalized);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── scopeLabel ──────────────────────────────────────────────────────────
// Returns the specific-name to show in the byline based on scope.
// Server already computes scope; we just look up the display name.
function scopeLabel(a, orgName) {
  if (a.scope === 'team') return a.team_name;
  if (a.scope === 'age_group') return a.age_group_name;
  return orgName; // 'org'
}

// ── AnnouncementCard ────────────────────────────────────────────────────
function AnnouncementCard({ announcement, orgName }) {
  const a = announcement;
  const muted = a.is_expired;
  const showPinned = a.pinned && !muted;

  return (
    <article
      className={`
        relative rounded-2xl bg-paper border border-rule
        px-5 py-4
        ${muted ? 'opacity-60' : ''}
      `}
    >
      {showPinned && (
        <Sticker
          tone="marker"
          className="absolute -top-2 right-4"
        >
          NEED TO KNOW!
        </Sticker>
      )}

      <h2 className="font-serif text-[22px] leading-tight text-ink pr-16">
        {showPinned ? (
          <Highlight color="yellow" rotate={-1}>
            {a.title}
          </Highlight>
        ) : (
          a.title
        )}
      </h2>

      <div className="mt-1.5 text-[12px] text-inkSub font-medium">
        {/* Byline: role · scope · time. Deferred: real author name. */}
        <span>Coach</span>
        <span className="mx-1.5">·</span>
        <span>{scopeLabel(a, orgName)}</span>
        <span className="mx-1.5">·</span>
        <span>{timeAgo(a.published_at)}</span>
      </div>

      <p className="mt-3 text-[14px] leading-relaxed text-ink whitespace-pre-wrap">
        {a.body}
      </p>
    </article>
  );
}

// ── AnnouncementsHome ───────────────────────────────────────────────────
export default function AnnouncementsHome() {
  const { activeOrg } = useOrg();
  const { data, loading, error } = useFetch('/announcements');

  // Coach + Admin can post. Guardian-only → no megaphone shown at all
  // (cleaner than a greyed-out affordance they can't use). Enforced
  // server-side too via requireRole(['Coach', 'Admin']) — this is just
  // the visibility gate.
  const canPost =
    activeOrg?.roles?.includes('Coach') ||
    activeOrg?.roles?.includes('Admin');
  return (
  // AppChromeLayout provides the app-level top bar (PfAppTopBar) and
  // drawer above this route, so PfShell here is just the mobile-frame
  // column — no inner chrome of its own.
  <PfShell showChrome={false}>
      <div className="px-5 pt-6 pb-8">
        <PageTitle
  eyebrow="ANNOUNCEMENTS"
  garnish={
    canPost ? (
      <Link
        to="/announcements/new"
        aria-label="Post a new announcement"
        className="inline-flex text-marker hover:text-ink transition-colors"
        style={{ transform: 'rotate(-3deg)' }}
      >
        <Megaphone size={44} />
      </Link>
    ) : null
  }
>
  <MarkerCircle color="marker" padding="loose">
    Need to know
  </MarkerCircle>
</PageTitle>

        <div className="mt-6 space-y-4">
          {loading && <SkeletonCard />}
          {error && <ErrorState error={error} />}
          {!loading && !error && data?.length === 0 && <EmptyState />}
          {!loading && !error && data?.map(a => (
            <AnnouncementCard
              key={a.id}
              announcement={a}
              orgName={activeOrg?.org_name ?? ''}
            />
          ))}
        </div>
      </div>
    </PfShell>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl bg-paper border border-rule px-5 py-4 animate-pulse">
      <div className="h-6 bg-rule rounded w-3/4" />
      <div className="mt-2 h-3 bg-rule rounded w-1/2" />
      <div className="mt-4 h-4 bg-rule rounded w-full" />
      <div className="mt-2 h-4 bg-rule rounded w-5/6" />
    </div>
  );
}

function ErrorState({ error }) {
  return (
    <div className="rounded-2xl bg-paper border border-rule px-5 py-6">
      <h3 className="font-serif text-[20px] text-ink">Something's off.</h3>
      <Scrawl className="mt-2" color="inkSub">
        couldn't load announcements — try refresh?
      </Scrawl>
      <details className="mt-3 text-[11px] text-inkSub">
        <summary className="cursor-pointer">details</summary>
        <pre className="mt-1 whitespace-pre-wrap">{String(error)}</pre>
      </details>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center pt-8 pb-4">
      <div className="font-serif text-[32px] text-ink leading-tight">
        Nothin' to see here!
      </div>
      <Scrawl className="mt-2 inline-block" color="inkSub">
        check back later 🏒
      </Scrawl>
    </div>
  );
}